import React, { useState, useEffect } from 'react';
import {
  Table,
  Card,
  Button,
  message,
  Switch,
  Space,
  Input,
  Modal,
  Form,
  InputNumber,
  Select,
  Spin
} from 'antd';
import {
  ReloadOutlined,
  EditOutlined,
  SearchOutlined
} from '@ant-design/icons';
import { supabase } from '../../api/supabase';
import theme from '../../theme/globalTheme';

const { Search } = Input;
const { Option } = Select;

function YachtManagement() {
  const [yachts, setYachts] = useState([]);
  const [filteredYachts, setFilteredYachts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [selectedYacht, setSelectedYacht] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [form] = Form.useForm();

  useEffect(() => {
    fetchYachts();
  }, []);

  const fetchYachts = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('yachts')
        .select('*')
        .order('display_order', { ascending: true });

      if (error) throw error;

      setYachts(data);
      setFilteredYachts(data);
    } catch (error) {
      message.error('Erreur lors du chargement des yachts: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value) => {
    setSearchText(value);
    const filtered = yachts.filter(yacht =>
      yacht.name?.toLowerCase().includes(value.toLowerCase()) ||
      yacht.brand?.toLowerCase().includes(value.toLowerCase())
    );
    setFilteredYachts(filtered);
  };

  const handleAvailabilityToggle = async (yacht, checked) => {
    try {
      const { error } = await supabase
        .from('yachts')
        .update({ is_available: checked })
        .eq('id', yacht.id);

      if (error) throw error;

      message.success(`${yacht.name} est maintenant ${checked ? 'disponible' : 'indisponible'}`);
      fetchYachts();
    } catch (error) {
      message.error('Erreur lors de la mise à jour: ' + error.message);
    }
  };

  const handleEdit = (yacht) => {
    setSelectedYacht(yacht);
    form.setFieldsValue({
      name: yacht.name,
      brand: yacht.brand,
      length_feet: yacht.length_feet,
      speed: yacht.speed,
      year: yacht.year,
      current_location: yacht.current_location,
      is_available: yacht.is_available,
      available_for_transfer: yacht.available_for_transfer,
      full_day_st_barth: yacht.full_day_st_barth,
      half_day_st_barth_morning: yacht.half_day_st_barth_morning,
      half_day_st_barth_afternoon: yacht.half_day_st_barth_afternoon,
      sunset_price: yacht.sunset_price,
      transfer_st_martin_to_st_barth: yacht.transfer_st_martin_to_st_barth,
      transfer_anguilla_to_st_barth: yacht.transfer_anguilla_to_st_barth,
      day_sxm_sbh_price: yacht.day_sxm_sbh_price,
      day_sbh_sxm_price: yacht.day_sbh_sxm_price,
    });
    setEditModalVisible(true);
  };

  const handleSaveEdit = async (values) => {
    try {
      const { error } = await supabase
        .from('yachts')
        .update(values)
        .eq('id', selectedYacht.id);

      if (error) throw error;

      message.success('Yacht mis à jour avec succès!');
      setEditModalVisible(false);
      fetchYachts();
    } catch (error) {
      message.error('Erreur lors de la mise à jour: ' + error.message);
    }
  };

  const columns = [
    {
      title: 'Nom',
      dataIndex: 'name',
      key: 'name',
      width: 120,
      fixed: 'left',
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: 'Marque',
      dataIndex: 'brand',
      key: 'brand',
      width: 100,
    },
    {
      title: 'Longueur (ft)',
      dataIndex: 'length_feet',
      key: 'length_feet',
      width: 100,
    },
    {
      title: 'Vitesse',
      dataIndex: 'speed',
      key: 'speed',
      width: 100,
    },
    {
      title: 'Location',
      dataIndex: 'current_location',
      key: 'current_location',
      width: 100,
    },
    {
      title: 'Disponible',
      dataIndex: 'is_available',
      key: 'is_available',
      width: 100,
      render: (available, record) => (
        <Switch
          checked={available}
          onChange={(checked) => handleAvailabilityToggle(record, checked)}
        />
      ),
    },
    {
      title: 'Transfert',
      dataIndex: 'available_for_transfer',
      key: 'available_for_transfer',
      width: 100,
      render: (available) => (
        <span style={{ color: available ? theme.colors.white : theme.colors.textTertiary }}>
          {available ? 'Oui' : 'Non'}
        </span>
      ),
    },
    {
      title: 'Full Day SBH',
      dataIndex: 'full_day_st_barth',
      key: 'full_day_st_barth',
      width: 120,
      render: (price) => price ? `${price}€` : '-',
    },
    {
      title: 'Half Day AM',
      dataIndex: 'half_day_st_barth_morning',
      key: 'half_day_st_barth_morning',
      width: 120,
      render: (price) => price ? `${price}€` : '-',
    },
    {
      title: 'Sunset',
      dataIndex: 'sunset_price',
      key: 'sunset_price',
      width: 100,
      render: (price) => price ? `${price}€` : '-',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      fixed: 'right',
      render: (_, record) => (
        <Button
          icon={<EditOutlined />}
          onClick={() => handleEdit(record)}
          size="small"
        >
          Modifier
        </Button>
      ),
    },
  ];

  return (
    <div style={{ minHeight: '100vh', background: theme.colors.bgLight, padding: '2rem' }}>
      <Card>
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 'bold' }}>Gestion de la Flotte</h1>
            <Space>
              <Input
                placeholder="Rechercher par nom ou marque"
                prefix={<SearchOutlined />}
                value={searchText}
                onChange={(e) => handleSearch(e.target.value)}
                style={{ width: 300 }}
                allowClear
              />
              <Button
                icon={<ReloadOutlined />}
                onClick={fetchYachts}
                loading={loading}
              >
                Actualiser
              </Button>
            </Space>
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '50px' }}>
              <Spin size="large" />
            </div>
          ) : (
            <Table
              columns={columns}
              dataSource={filteredYachts}
              rowKey="id"
              bordered
              pagination={{ pageSize: 20 }}
              scroll={{ x: 1500 }}
            />
          )}
        </Space>
      </Card>

      {/* Modal d'édition */}
      <Modal
        title={`Modifier ${selectedYacht?.name}`}
        open={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        footer={null}
        width={800}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSaveEdit}
        >
          <Form.Item
            name="name"
            label="Nom du Yacht"
            rules={[{ required: true, message: 'Requis' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="brand"
            label="Marque"
            rules={[{ required: true, message: 'Requis' }]}
          >
            <Input />
          </Form.Item>

          <Space style={{ width: '100%' }} size="middle">
            <Form.Item
              name="length_feet"
              label="Longueur (ft)"
            >
              <InputNumber style={{ width: 150 }} />
            </Form.Item>

            <Form.Item
              name="speed"
              label="Vitesse"
            >
              <Input style={{ width: 150 }} />
            </Form.Item>

            <Form.Item
              name="year"
              label="Année"
            >
              <InputNumber style={{ width: 150 }} />
            </Form.Item>
          </Space>

          <Form.Item
            name="current_location"
            label="Location Actuelle"
          >
            <Select style={{ width: 200 }}>
              <Option value="SBH">SBH</Option>
              <Option value="SXM">SXM</Option>
              <Option value="AXA">AXA</Option>
              <Option value="St Kitts">St Kitts</Option>
            </Select>
          </Form.Item>

          <Space style={{ width: '100%' }}>
            <Form.Item
              name="is_available"
              label="Disponible"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>

            <Form.Item
              name="available_for_transfer"
              label="Disponible pour Transfert"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
          </Space>

          <h3>Prix</h3>

          <Space style={{ width: '100%' }} size="middle" wrap>
            <Form.Item
              name="full_day_st_barth"
              label="Full Day SBH"
            >
              <InputNumber prefix="€" style={{ width: 150 }} />
            </Form.Item>

            <Form.Item
              name="half_day_st_barth_morning"
              label="Half Day AM"
            >
              <InputNumber prefix="€" style={{ width: 150 }} />
            </Form.Item>

            <Form.Item
              name="half_day_st_barth_afternoon"
              label="Half Day PM"
            >
              <InputNumber prefix="€" style={{ width: 150 }} />
            </Form.Item>

            <Form.Item
              name="sunset_price"
              label="Sunset"
            >
              <InputNumber prefix="€" style={{ width: 150 }} />
            </Form.Item>
          </Space>

          <Space style={{ width: '100%' }} size="middle" wrap>
            <Form.Item
              name="transfer_st_martin_to_st_barth"
              label="Transfert SXM → SBH"
            >
              <InputNumber prefix="€" style={{ width: 150 }} />
            </Form.Item>

            <Form.Item
              name="transfer_anguilla_to_st_barth"
              label="Transfert AXA → SBH"
            >
              <InputNumber prefix="€" style={{ width: 150 }} />
            </Form.Item>

            <Form.Item
              name="day_sxm_sbh_price"
              label="Day SXM-SBH"
            >
              <InputNumber prefix="€" style={{ width: 150 }} />
            </Form.Item>

            <Form.Item
              name="day_sbh_sxm_price"
              label="Day SBH-SXM"
            >
              <InputNumber prefix="€" style={{ width: 150 }} />
            </Form.Item>
          </Space>

          <div style={{ marginTop: 20, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setEditModalVisible(false)}>
                Annuler
              </Button>
              <Button type="primary" htmlType="submit">
                Enregistrer
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>
    </div>
  );
}

export default YachtManagement;
